import { AxiosError } from 'axios';
import React from 'react';
import { ErrorHandler, ValidationError } from '../utils/errorHandler';

interface ErrorMessageProps {
    error: AxiosError | string | null;
    context?: string;
    className?: string;
    showValidationErrors?: boolean;
    showCode?: boolean;
    messageOnly?: boolean;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
    error,
    context,
    className = '',
    showValidationErrors = true,
    showCode = true,
    messageOnly = false
}) => {
    if (!error) return null;

    // Handle string errors (legacy support)
    if (typeof error === 'string') {
        if (messageOnly) {
            return (
                <div className={`bg-error-50 border border-error-200 rounded-lg p-4 ${className}`}>
                    <div className="text-sm text-error-600">{error}</div>
                </div>
            );
        }
        return (
            <div className={`bg-error-50 border border-error-200 rounded-lg p-4 ${className}`}>
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-error-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-error-700">
                            Error{context ? ` in ${context}` : ''}
                        </h3>
                        <div className="mt-2 text-sm text-error-600">
                            <p>{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Handle AxiosError
    const formattedError = ErrorHandler.formatError(error);
    const { message, code, isValidation, validationErrors } = formattedError;

    // messageOnly path for Axios errors
    if (messageOnly) {
        return (
            <div className={`bg-error-50 border border-error-200 rounded-lg p-4 ${className}`}>
                <div className="text-sm text-error-600">{message}</div>
            </div>
        );
    }

    // Friendly labels for known fields
    const fieldLabelMap: Record<string, string> = {
        wine_batch_id: 'Wine batch',
        fermentation_id: 'Fermentation',
        wine_type: 'Wine type',
        start_date: 'Start date',
        batch_name: 'Batch name',
        batch_id: 'Batch ID',
        vineyard_id: 'Vineyard',
        responsible_user_id: 'Responsible user',
        end_date: 'End date',
        status: 'Status',
        weight: 'Weight',
        net_liquid_weight: 'Net liquid weight',
        final_brix: 'Final Brix',
        final_ph: 'Final pH',
        final_ta: 'Final TA',
        final_sg: 'Final SG',
        notes: 'Notes',
        name: 'Name',
        location: 'Location',
        planted_variety: 'Planted variety',
        planting_date: 'Planting date',
        acreage: 'Acreage',
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        password: 'Password',
        roleId: 'Role',
        vessel_id: 'Vessel',
        measurement_date: 'Measurement date',
        temperature: 'Temperature',
        ph: 'pH',
        brix: 'Brix',
        ta: 'TA',
        sg: 'SG',
        yeast_strain: 'Yeast strain',
        yeast_amount: 'Yeast amount',
        nutrient_type: 'Nutrient type',
        nutrient_amount: 'Nutrient amount',
        punch_down_frequency: 'Punch down frequency',
        pump_over_frequency: 'Pump over frequency',
        cap_management: 'Cap management',
        malolactic_fermentation: 'Malolactic fermentation',
        mlb_strain: 'MLB strain',
        mlb_amount: 'MLB amount',
        mlb_temperature: 'MLB temperature',
        mlb_duration: 'MLB duration',
        barrel_type: 'Barrel type',
        barrel_age: 'Barrel age',
        oak_level: 'Oak level',
        aging_duration: 'Aging duration',
        racking_frequency: 'Racking frequency',
        topping_frequency: 'Topping frequency',
        bottling_date: 'Bottling date',
        bottle_type: 'Bottle type',
        closure_type: 'Closure type',
        label_design: 'Label design',
        packaging_notes: 'Packaging notes'
    };

    // Translate technical validation messages to friendly copy, or hide if not useful
    const translateValidation = (err: ValidationError): { label: string; text: string } | null => {
        const field = err.field || 'unknown';
        const raw = (err.message || '').toLowerCase();
        const label = fieldLabelMap[field] || field.replace(/_/g, ' ');

        // Hide fermentation_id errors (internal field; end users choose the batch)
        if (field === 'fermentation_id') {
            return null;
        }

        if (field === 'wine_batch_id') {
            if (raw.includes('must be an integer') || raw.includes('invalid') || raw.includes('required')) {
                return { label: 'Wine batch', text: 'Please select a wine batch.' };
            }
        }

        // Default: return original message with friendlier label
        return { label, text: err.message };
    };

    const friendlyValidationErrors = (validationErrors || [])
        .map(translateValidation)
        .filter((v): v is { label: string; text: string } => Boolean(v));

    return (
        <div className={`bg-error-50 border border-error-200 rounded-lg p-4 ${className}`}>
            <div className="flex">
                <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-error-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="ml-3">
                    {/* Only show the main error message if there are no specific validation errors */}
                    {!(showValidationErrors && isValidation && friendlyValidationErrors.length > 0) && (
                        <h3 className="text-sm font-medium text-error-700">
                            Error{context ? ` in ${context}` : ''}
                            {showCode && code && (
                                <span className="ml-2 text-xs text-error-500 font-normal">
                                    ({code})
                                </span>
                            )}
                        </h3>
                    )}
                    <div className="mt-2 text-sm text-error-600">
                        {/* Only show the generic message if there are no specific validation errors */}
                        {!(showValidationErrors && isValidation && friendlyValidationErrors.length > 0) && (
                            <p>{message}</p>
                        )}
                        {showValidationErrors && isValidation && friendlyValidationErrors.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-error-700 mb-2">
                                    Please fix the following issues:
                                </h4>
                                <ul className="list-disc list-inside space-y-1">
                                    {friendlyValidationErrors.map((fv, index: number) => (
                                        <li key={index} className="text-sm text-error-600">
                                            <span className="font-medium">{fv.label}:</span> {fv.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ErrorMessage;
